import { useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import { Button, Checkbox, Grid, Stack, Typography } from "@financeable-com-au/financeable-ui";
import {
  INVOICE_BILLING_MONTHS,
  INVOICE_GST_RATE,
  INVOICE_LOCKED_CC_EMAIL,
  INVOICE_STATUS,
  ROUTES,
  SUCCESS_MESSAGE,
} from "@/constants";
import { tenantStateSelector, getAllTenantDetailsList, updateTenant } from "@/store/slices/tenantSlice";
import {
  getTenantServiceUsages,
  getInvoiceById,
  invoiceStateSelector,
  resetTenantInvoiceDetails,
  resetInvoiceForm,
  saveInvoiceDraft,
  sendInvoice,
  setInvoiceExpandedSection,
  setInvoiceForm,
  setInvoiceShowErrors,
  updateInvoiceDraft,
} from "@/store/slices/invoiceSlice";
import { AppDispatch } from "@/store/store";
import { ITenantSlice, ITenantState } from "@/store/slices/types/tenantSliceTypes";
import {
  IInvoiceExpandedSections,
  IInvoiceRecipient,
  IInvoiceSlice,
  IInvoiceState,
} from "@/store/slices/types/invoiceSliceTypes";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { IDispatchResponse, TenantRecipientPatch } from "@/utils/interface";
import { getErrorToastPayload, getSuccessToastPayload } from "@/utils/commonFunctions";
// @ts-expect-error: ignoring as it is existing js file
import regex from "@/utils/regex";
import { Loader } from "@/components/Loader";
import InvoiceInfo from "./components/InvoiceInfo";
import RecipientSection from "./components/RecipientSection";
import FeesItemsSection from "./components/FeesItemsSection";
import InvoiceNotes from "./components/InvoiceNotes";
import ServiceUsagesSection from "./components/ServiceUsagesSection";
import EmailPreview from "./components/EmailPreview";
import Summary from "./components/Summary";
import RequiredFieldsTooltip from "./components/RequiredFieldsTooltip";
import { TenantOption } from "./types";

const isValidEmail = (email: string) => regex.email.test(email);
// The current send endpoint safely resends only open non-Stripe invoices. Stripe resends need a backend path
// that reuses the original Stripe invoice instead of creating a new one.
const RESENDABLE_INVOICE_STATUSES = [INVOICE_STATUS.open];

interface InvoiceActionNoteProps {
  children: React.ReactNode;
  intent?: "danger" | "neutral";
  bold?: boolean;
}

const InvoiceActionNote = ({ children, intent = "danger", bold = true }: InvoiceActionNoteProps) => (
  <Typography
    level="body-xs"
    textColor={intent === "danger" ? "danger.500" : "neutral.500"}
    style={{ textAlign: "right", ...(bold ? { fontWeight: 700 } : {}) }}
  >
    {children}
  </Typography>
);

const InvoiceDetails = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const isEdit = Boolean(invoiceId && invoiceId !== "new");
  const tenantsFetchedRef = useRef(false);
  const skipInitialUsageFetchRef = useRef(isEdit);
  // Last value persisted to the tenant per recipient field, used to skip redundant blur saves.
  const recipientSavedRef = useRef<Record<string, string>>({});

  const tenantState = useSelector<ITenantSlice, ITenantState>(tenantStateSelector);
  const invoiceState = useSelector<IInvoiceSlice, IInvoiceState>(invoiceStateSelector);
  const { tenantDetailsList, gettingTenantsDropdownList } = tenantState;
  const {
    tenantDetails,
    gettingTenantDetails,
    tenantServiceUsages,
    gettingServiceUsages,
    savingInvoice,
    sendingInvoice,
    form,
    showErrors,
    expandedSections,
    gettingInvoiceById,
  } = invoiceState;
  const invoiceStatus = (form?.status || "").toLowerCase();
  const isDraftInvoice = invoiceStatus === INVOICE_STATUS.draft;
  const isDisabledEdit = !isDraftInvoice;
  const canResendInvoice =
    isEdit && Boolean(invoiceId) && RESENDABLE_INVOICE_STATUSES.includes(invoiceStatus) && !form.isStripeInvoice;
  const invoiceActionNotes = useMemo(() => {
    const notes: string[] = [];

    if (!isDraftInvoice) {
      notes.push("Only invoices with a Draft status can be updated.");
    }

    if (invoiceStatus === INVOICE_STATUS.paid) {
      notes.push("Paid invoices can't be resent.");
    } else if (!isDraftInvoice && form.isStripeInvoice) {
      notes.push("Stripe invoices can't be resent.");
    }

    return notes;
  }, [form.isStripeInvoice, invoiceStatus, isDraftInvoice]);

  const updateForm = (patch: Partial<typeof form>) => dispatch(setInvoiceForm(patch));
  const setSectionExpanded = (key: keyof IInvoiceExpandedSections) => (value: boolean) =>
    dispatch(setInvoiceExpandedSection({ key, value }));

  useEffect(() => {
    if (tenantsFetchedRef.current) return;
    tenantsFetchedRef.current = true;
    dispatch(getAllTenantDetailsList(null));
  }, [dispatch]);

  useEffect(() => {
    if (isEdit && invoiceId) {
      dispatch(getInvoiceById(invoiceId));
    } else {
      dispatch(resetInvoiceForm());
    }
    return () => {
      dispatch(resetTenantInvoiceDetails());
      dispatch(resetInvoiceForm());
    };
  }, [dispatch, invoiceId, isEdit]);

  useEffect(() => {
    if (!tenantDetails) return;
    const feeItems = (tenantDetails.feeItems || []).map((li, idx) => ({
      ...li,
      id: `pre-${idx}-${Date.now()}`,
    }));
    dispatch(setInvoiceForm({ feeItems }));
  }, [tenantDetails, dispatch]);

  useEffect(() => {
    if (!tenantServiceUsages?.feesItems) return;
    const feeItems = tenantServiceUsages.feesItems.map((li, idx) => ({
      ...li,
      id: `usage-${idx}-${Date.now()}`,
    }));
    dispatch(
      setInvoiceForm({
        feeItems,
        isAutoChargeEnabled: false,
      }),
    );
    dispatch(setInvoiceExpandedSection({ key: "feesItems", value: true }));
    dispatch(setInvoiceExpandedSection({ key: "serviceUsages", value: true }));
  }, [tenantServiceUsages, dispatch]);

  const tenantOptions: TenantOption[] = useMemo(
    () =>
      (tenantDetailsList || []).map((c) => {
        const fullName = [c.firstName, c.lastName].filter(Boolean).join(" ");
        return {
          label: fullName ? `${c.clientConnection?.name} - ${fullName}` : c.clientConnection?.name || "",
          value: c._id,
        };
      }),
    [tenantDetailsList],
  );

  const sortedTenantOptions = useMemo(() => {
    return [...tenantOptions].sort((a, b) => a.label.localeCompare(b.label));
  }, [tenantOptions]);

  const subtotal = useMemo(
    () => form.feeItems.reduce((acc, li) => acc + (li.qty || 0) * (li.unitPriceDollars || 0), 0),
    [form.feeItems],
  );
  const gst = subtotal * INVOICE_GST_RATE;
  const total = subtotal + gst;

  const billingMonthLabel = useMemo(
    () => INVOICE_BILLING_MONTHS.find((m) => m.value === form.billingMonth)?.label || "",
    [form.billingMonth],
  );

  const errors = useMemo(() => {
    return {
      invoiceTitle: form.invoiceTitle.trim() ? undefined : "Required",
      tenancyBillingConfigId: form.tenancyBillingConfigId ? undefined : "Required",
      recipientFirstName: form.recipient.firstName.trim() ? undefined : "Required",
      recipientLastName: form.recipient.lastName.trim() ? undefined : "Required",
      recipientEmail: isValidEmail(form.recipient.email) ? undefined : "Valid email required",
    };
  }, [form]);

  const hasErrors = Object.values(errors).some(Boolean);

  const remainingRequiredFields = useMemo(() => {
    const list: string[] = [];
    if (errors.invoiceTitle) list.push("Invoice title");
    if (errors.tenancyBillingConfigId) list.push("Tenant");
    if (errors.recipientFirstName) list.push("First name");
    if (errors.recipientLastName) list.push("Last name");
    if (errors.recipientEmail) list.push("Recipient email");
    return list;
  }, [errors]);

  const visibleErrors = showErrors ? errors : undefined;
  const tenantLocked = !form.tenancyBillingConfigId;

  useEffect(() => {
    if (!form.tenancyBillingConfigId) return;
    if (skipInitialUsageFetchRef.current) {
      skipInitialUsageFetchRef.current = false;
      return;
    }
    dispatch(
      getTenantServiceUsages({
        tenancyBillingConfigId: form.tenancyBillingConfigId,
        month: form.billingMonth,
        year: form.billingYear,
      }),
    );
  }, [form.billingMonth, form.billingYear, form.tenancyBillingConfigId, dispatch]);

  const buildPayload = (status?: string) => {
    return {
      ...(invoiceId && invoiceId !== "new" && { invoiceId }),
      title: form.invoiceTitle.trim(),
      service_usage_ids: tenantServiceUsages?.serviceUsageIds || [],
      status: status || INVOICE_STATUS.draft,
      ccEmails: [INVOICE_LOCKED_CC_EMAIL, ...form.ccEmails],
      issuedDate: form.issuedDate,
      month: form.billingMonth,
      year: form.billingYear,
      total: Number(total.toFixed(2)),
      fees: form.feeItems.map(({ id: _id, ...rest }) => rest),
      notes: form.notes,
      tenancyBillingConfig: form.tenancyBillingConfigId,
      isAutoChargeEnabled: form.isAutoChargeEnabled,
      isStripeInvoice: form.isStripeInvoice,
    };
  };

  const handleTenantSelect = (tenancyBillingConfigId: string | null) => {
    const selected = tenancyBillingConfigId
      ? (tenantDetailsList || []).find((c) => c._id === tenancyBillingConfigId)
      : null;
    dispatch(
      setInvoiceForm({
        tenancyBillingConfigId,
        recipient: selected
          ? {
              firstName: selected.firstName || "",
              lastName: selected.lastName || "",
              email: selected.email || "",
              billingName: selected.billingName || "",
              address: selected.address || "",
              billingAddress: selected.billingAddress || "",
            }
          : { firstName: "", lastName: "", email: "", billingName: "", address: "", billingAddress: "" },
        ccEmails: selected ? (selected.ccEmails || []).filter((e) => e !== INVOICE_LOCKED_CC_EMAIL) : [],
        feeItems: tenancyBillingConfigId ? form.feeItems : [],
      }),
    );
    if (!tenancyBillingConfigId) {
      dispatch(resetTenantInvoiceDetails());
    }
  };

  const RECIPIENT_STRING_FIELDS = [
    "firstName",
    "lastName",
    "email",
    "billingName",
    "address",
    "billingAddress",
  ] as const;

  const buildRecipientSavedMap = (recipient: IInvoiceRecipient, ccEmails: string[]): Record<string, string> => ({
    firstName: recipient.firstName.trim(),
    lastName: recipient.lastName.trim(),
    email: recipient.email.trim(),
    billingName: (recipient.billingName || "").trim(),
    address: (recipient.address || "").trim(),
    billingAddress: (recipient.billingAddress || "").trim(),
    ccEmails: JSON.stringify(ccEmails),
  });

  // Reseed the per-field baseline whenever the selected tenant changes so an unedited blur doesn't trigger a save.
  useEffect(() => {
    recipientSavedRef.current = form.tenancyBillingConfigId
      ? buildRecipientSavedMap(form.recipient, form.ccEmails)
      : {};
  }, [form.tenancyBillingConfigId]);

  // Persists a single edited recipient field back to the tenant on blur. Skips when locked, invalid, or unchanged.
  const handleRecipientSave = (patch: TenantRecipientPatch) => {
    const tenantId = form.tenancyBillingConfigId;
    if (!tenantId) return;
    if (patch.firstName !== undefined && !patch.firstName.trim()) return;
    if (patch.lastName !== undefined && !patch.lastName.trim()) return;
    if (patch.email !== undefined && !isValidEmail(patch.email)) return;

    const saved = recipientSavedRef.current;
    const payload: TenantRecipientPatch = {};

    RECIPIENT_STRING_FIELDS.forEach((field) => {
      const incoming = patch[field];
      if (incoming === undefined) return;
      const next = incoming.trim();
      if (saved[field] !== next) {
        payload[field] = next;
        saved[field] = next;
      }
    });

    if (patch.ccEmails !== undefined) {
      const next = JSON.stringify(patch.ccEmails);
      if (saved.ccEmails !== next) {
        payload.ccEmails = patch.ccEmails;
        saved.ccEmails = next;
      }
    }

    if (Object.keys(payload).length === 0) return;
    dispatch(updateTenant({ tenantId, payload })).then((res: IDispatchResponse) => {
      if (res?.error) {
        // Drop the baselines we just set so the failed field(s) can be retried.
        Object.keys(payload).forEach((field) => delete saved[field]);
        dispatch(
          openSnackbar({
            message: res?.payload?.status_message || "Failed to update tenant details",
            intent: "danger",
          }),
        );
      } else {
        dispatch(
          openSnackbar({
            message: res?.payload?.data?.status_message || SUCCESS_MESSAGE.tenantUpdated,
            intent: "success",
          }),
        );
      }
    });
  };

  const showInvoiceActionResult = (
    res: IDispatchResponse,
    successFallbackMessage: string,
    errorFallbackMessage: string,
    options?: { useSuccessFallbackMessage?: boolean },
  ) => {
    let toastPayload: ReturnType<typeof getSuccessToastPayload>;

    if (res?.error) {
      toastPayload = getErrorToastPayload(res, errorFallbackMessage);
    } else if (options?.useSuccessFallbackMessage) {
      toastPayload = { message: successFallbackMessage, intent: "success" } as const;
    } else {
      toastPayload = getSuccessToastPayload(res, successFallbackMessage);
    }

    dispatch(openSnackbar(toastPayload));
    return !res?.error;
  };

  const handleAfterSubmit = (res: IDispatchResponse, successFallbackMessage: string, errorFallbackMessage: string) => {
    if (showInvoiceActionResult(res, successFallbackMessage, errorFallbackMessage)) {
      setTimeout(() => {
        navigate(ROUTES.ADMIN.INVOICES);
      }, 300);
    }
  };

  const handleSaveDraft = () => {
    if (hasErrors) {
      dispatch(setInvoiceShowErrors(true));
      return;
    }
    const payload = buildPayload();
    const action =
      isEdit && invoiceId ? dispatch(updateInvoiceDraft({ invoiceId, payload })) : dispatch(saveInvoiceDraft(payload));
    action.then((res: IDispatchResponse) =>
      handleAfterSubmit(res, SUCCESS_MESSAGE.invoiceGenerated, "Failed to save invoice draft"),
    );
  };

  const handleSendInvoice = () => {
    if (hasErrors) {
      dispatch(setInvoiceShowErrors(true));
      return;
    }
    const payload = buildPayload();
    const action = dispatch(sendInvoice(payload));
    action.then((res: IDispatchResponse) =>
      handleAfterSubmit(res, SUCCESS_MESSAGE.invoiceGenerated, "Failed to send invoice"),
    );
  };

  const handleResendInvoice = () => {
    if (!invoiceId || invoiceId === "new" || !canResendInvoice) return;
    if (hasErrors) {
      dispatch(setInvoiceShowErrors(true));
      return;
    }

    dispatch(sendInvoice(buildPayload(INVOICE_STATUS.open))).then((res: IDispatchResponse) => {
      showInvoiceActionResult(res, SUCCESS_MESSAGE.invoiceResent, "Failed to resend invoice", {
        useSuccessFallbackMessage: true,
      });
    });
  };

  const pageTitle = isEdit ? "Edit invoice" : "Create invoice";
  const loadingInvoice = isEdit && gettingInvoiceById;

  return (
    <Stack direction="column" rowGap={4} sx={{ padding: 6, backgroundColor: "neutral.50", minHeight: "100vh" }}>
      {gettingInvoiceById ? (
        <>
          <Loader text="Loading Invoice..." />
        </>
      ) : (
        <>
          <Stack direction="row" alignItems="center" columnGap={2}>
            <Typography level="h2" style={{ fontSize: "24px", fontWeight: 800 }}>
              {pageTitle}
            </Typography>
          </Stack>

          <Grid container spacing={4}>
            <Grid xs={12} md={7} lg={8}>
              <Stack direction="column" rowGap={4}>
                <InvoiceInfo
                  value={form}
                  tenants={sortedTenantOptions}
                  loadingTenants={gettingTenantsDropdownList}
                  disabled={isDisabledEdit || loadingInvoice}
                  errors={visibleErrors}
                  expanded={expandedSections.invoiceInfo}
                  onExpandedChange={setSectionExpanded("invoiceInfo")}
                  onChange={updateForm}
                  onTenantSelect={handleTenantSelect}
                />
                <RecipientSection
                  disabled={isDisabledEdit || tenantLocked || gettingTenantDetails || loadingInvoice}
                  value={form}
                  errors={visibleErrors}
                  expanded={expandedSections.recipient}
                  onExpandedChange={setSectionExpanded("recipient")}
                  onChange={updateForm}
                  onSave={handleRecipientSave}
                />
                <FeesItemsSection
                  disabled={isDisabledEdit || tenantLocked || gettingTenantDetails || loadingInvoice}
                  items={form.feeItems}
                  loading={gettingServiceUsages || loadingInvoice}
                  expanded={expandedSections.feesItems}
                  isDraftInvoice={isDraftInvoice}
                  onExpandedChange={setSectionExpanded("feesItems")}
                  onChange={(items) => updateForm({ feeItems: items })}
                />
                <ServiceUsagesSection
                  disabled={tenantLocked}
                  loading={gettingServiceUsages}
                  usages={tenantServiceUsages}
                  expanded={expandedSections.serviceUsages}
                  onExpandedChange={setSectionExpanded("serviceUsages")}
                />
                <InvoiceNotes
                  value={form.notes}
                  onChange={(v) => updateForm({ notes: v })}
                  disabled={isDisabledEdit || tenantLocked || gettingTenantDetails || loadingInvoice}
                />
              </Stack>
            </Grid>

            <Grid xs={12} md={5} lg={4}>
              <Stack direction="column" rowGap={4}>
                <EmailPreview
                  invoiceTitle={form.invoiceTitle}
                  recipientEmail={form.recipient.email}
                  recipientFirstName={form.recipient.firstName}
                  recipientLastName={form.recipient.lastName}
                  ccEmails={form.ccEmails}
                  billingMonth={billingMonthLabel}
                  billingYear={form.billingYear}
                  total={total}
                  loading={gettingServiceUsages || loadingInvoice}
                />
                <Summary
                  subtotal={subtotal}
                  gst={gst}
                  total={total}
                  billingMonth={billingMonthLabel}
                  billingYear={form.billingYear}
                  loading={gettingServiceUsages || loadingInvoice}
                />
                {!isDisabledEdit && (
                  <>
                    <Stack direction="row" justifyContent="flex-end">
                      <Checkbox
                        label="Auto charge customer"
                        size="sm"
                        name="isAutoChargeEnabled"
                        checked={form.isAutoChargeEnabled}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          updateForm({ isAutoChargeEnabled: e.target.checked })
                        }
                        disabled={
                          !tenantServiceUsages?.isAutoChargeEnabled ||
                          tenantLocked ||
                          gettingServiceUsages ||
                          loadingInvoice ||
                          !form.isStripeInvoice
                        }
                      />
                    </Stack>
                    <Stack direction="row" justifyContent="flex-end">
                      <Checkbox
                        label="Stripe invoice"
                        size="sm"
                        name="isStripeInvoice"
                        checked={form.isStripeInvoice}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          updateForm({ isStripeInvoice: e.target.checked })
                        }
                        disabled={tenantLocked || gettingServiceUsages || loadingInvoice}
                      />
                    </Stack>
                    <Stack direction="row" columnGap={2} justifyContent="flex-end" alignItems="center">
                      <Button
                        variant="secondary"
                        startDecorator={<SaveIcon />}
                        loading={savingInvoice}
                        disabled={hasErrors || savingInvoice || loadingInvoice}
                        onClick={handleSaveDraft}
                      >
                        Save as draft
                      </Button>
                      <Button
                        variant="primary"
                        startDecorator={<SendIcon />}
                        loading={sendingInvoice}
                        disabled={hasErrors || sendingInvoice || loadingInvoice}
                        onClick={handleSendInvoice}
                      >
                        {isEdit ? "Update & send" : "Send invoice"}
                      </Button>
                      {hasErrors && (
                        <RequiredFieldsTooltip
                          fields={remainingRequiredFields}
                          onShowErrors={() => dispatch(setInvoiceShowErrors(true))}
                        />
                      )}
                    </Stack>
                  </>
                )}
                {canResendInvoice && (
                  <Stack direction="row" columnGap={2} justifyContent="flex-end" alignItems="center">
                    <Button
                      variant="primary"
                      startDecorator={<SendIcon />}
                      loading={sendingInvoice}
                      disabled={hasErrors || sendingInvoice || loadingInvoice}
                      onClick={handleResendInvoice}
                    >
                      Resend invoice
                    </Button>
                    {hasErrors && (
                      <RequiredFieldsTooltip
                        fields={remainingRequiredFields}
                        onShowErrors={() => dispatch(setInvoiceShowErrors(true))}
                      />
                    )}
                  </Stack>
                )}
                {invoiceActionNotes.map((note) => (
                  <InvoiceActionNote key={note}>{note}</InvoiceActionNote>
                ))}
                {!isDisabledEdit && (
                  <InvoiceActionNote intent="neutral" bold={false}>
                    <Typography component="span" style={{ fontWeight: 700 }}>
                      Save as draft
                    </Typography>{" "}
                    stores it for later review.
                  </InvoiceActionNote>
                )}
              </Stack>
            </Grid>
          </Grid>
        </>
      )}
    </Stack>
  );
};

export default InvoiceDetails;
